import TrimbleMaps, { GeoJSONSourceRaw } from "@trimblemaps/trimblemaps-js";
import { DEFAULT_MAP_CENTER, ROUTE_OPTIONS } from "config/mapping";
import { BasicRoute, Coordinate, FuelLocation, RouteStop } from "types";

export type FuelStopMapOptions = {
  selectedFuelStopIds?: string[];
  onSelectFuelStop?: (fuelStopId: string) => void;
};

export const createRoute = (
  routeId: string,
  stops: RouteStop[] | Coordinate[],
  setIds: number[] = []
): TrimbleMaps.Route => {
  return new TrimbleMaps.Route({
    routeId: routeId,
    afSetIDs: setIds,
    stops: convertStopsToTrimbleLngLat(stops),
    ...ROUTE_OPTIONS,
  });
};

const convertStopsToTrimbleLngLat = (stops: RouteStop[] | Coordinate[]) => {
  const retVal: TrimbleMaps.LngLat[] = [];
  if (stops && Array.isArray(stops)) {
    stops.forEach((stop) => {
      if (stop.lat != null && stop.lon !== null) {
        retVal.push(new TrimbleMaps.LngLat(stop.lon, stop.lat));
      }
    });
  }
  return retVal;
};

const convertPreferredFuelStopsToGeoJSON = (
  preferredFuelStops: FuelLocation[],
  selectedFuelStopIds: string[] = []
): GeoJSONSourceRaw => {
  let features: GeoJSON.Feature[] = [];
  const selectedIds = selectedFuelStopIds.map(String);

  if (preferredFuelStops && preferredFuelStops.length > 0) {
    preferredFuelStops.forEach((preferredFuelStop) => {
      const address = preferredFuelStop.address;

      features.push({
        type: "Feature",
        properties: {
          id: String(preferredFuelStop.id),
          isSelected: selectedIds.includes(String(preferredFuelStop.id)),
          name: preferredFuelStop.name,
          street: address?.street ?? "",
          city: address?.city ?? "",
          state: address?.state ?? "",
          zip: address?.zip ?? "",
          route: preferredFuelStop.route ?? "",
          exit: preferredFuelStop.exit ?? "",
        } as GeoJSON.GeoJsonProperties,
        geometry: {
          type: "Point",
          coordinates: [
            preferredFuelStop.coords.lon,
            preferredFuelStop.coords.lat,
          ],
        } as GeoJSON.Geometry,
      } as GeoJSON.Feature);
    });
  }

  return {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: features,
    },
  } as GeoJSONSourceRaw;
};

const appendPopupText = (
  container: HTMLElement,
  className: string,
  text: string
): void => {
  if (!text) return;

  const element = document.createElement("div");
  element.className = className;
  element.textContent = text;
  container.appendChild(element);
};

export const createFuelStopPopupContent = (
  properties: GeoJSON.GeoJsonProperties,
  onSelectFuelStop?: (fuelStopId: string) => void
): HTMLElement => {
  const container = document.createElement("div");
  const id = String(properties?.id ?? "");
  const name = String(properties?.name ?? "Fuel stop");
  const city = String(properties?.city ?? "");
  const state = String(properties?.state ?? "");
  const zip = String(properties?.zip ?? "");
  const cityState = [city, state].filter(Boolean).join(", ");
  const location = [cityState, zip].filter(Boolean).join(" ");

  appendPopupText(container, "poi-popup-header", name);
  appendPopupText(
    container,
    "poi-popup-address",
    String(properties?.street ?? "")
  );
  appendPopupText(container, "poi-popup-address", location);
  appendPopupText(
    container,
    "poi-popup-address",
    properties?.route ? `Route: ${String(properties.route)}` : ""
  );
  appendPopupText(
    container,
    "poi-popup-address",
    properties?.exit ? `Exit: ${String(properties.exit)}` : ""
  );

  if (properties?.isSelected === true) {
    appendPopupText(container, "poi-popup-selected", "Currently selected");
  }

  if (onSelectFuelStop && id && properties?.isSelected !== true) {
    const selectButton = document.createElement("button");
    selectButton.className = "poi-popup-select";
    selectButton.type = "button";
    selectButton.textContent = "Use this fuel stop";
    selectButton.setAttribute("aria-label", `Use ${name} as the fuel stop`);
    selectButton.addEventListener("click", () => onSelectFuelStop(id));
    container.appendChild(selectButton);
  }

  return container;
};

export const createMap = (
  currentMap: TrimbleMaps.Map | undefined
): TrimbleMaps.Map => {
  if (currentMap) {
    currentMap.remove();
  }

  const map = new TrimbleMaps.Map({
    container: "map",
    center: DEFAULT_MAP_CENTER,
    renderWorldCopies: false,
  });

  map.addControl(
    new TrimbleMaps.NavigationControl({
      showCompass: false,
    }),
    "top-right"
  );

  map.addControl(
    new TrimbleMaps.ScaleControl({
      maxWidth: 80,
      unit: "imperial",
    })
  );

  return map;
};

export const addPreferredFuelStopsToMap = (
  map: TrimbleMaps.Map,
  preferredFuelStops: FuelLocation[],
  fuelDCLocations: FuelLocation[],
  options: FuelStopMapOptions = {}
): void => {
  const poiPoints: FuelLocation[] = preferredFuelStops.concat(fuelDCLocations);
  const selectedFuelStopIds = options.selectedFuelStopIds?.map(String) ?? [];
  if (poiPoints.length <= 0) return;

  if (map.getLayer("selectedPoiPoint")) {
    map.removeLayer("selectedPoiPoint");
  }
  if (map.getLayer("poiPoints")) {
    map.removeLayer("poiPoints");
    map.removeSource("poiSource");
    map.removeImage("poi-marker-icon");
  }

  if (poiPoints && Array.isArray(poiPoints)) {
    map.addSource(
      "poiSource",
      convertPreferredFuelStopsToGeoJSON(poiPoints, selectedFuelStopIds)
    );
    map.loadImage(
      `${process.env.PUBLIC_URL}/fuel.png`,
      (
        error: TrimbleMaps.ErrorEvent,
        image: HTMLImageElement | ArrayBufferView
      ) => {
        if (error) return;

        map.addImage("poi-marker-icon", image);

        if (selectedFuelStopIds.length > 0) {
          map.addLayer({
            id: "selectedPoiPoint",
            type: "circle",
            source: "poiSource",
            filter: ["in", ["get", "id"], ["literal", selectedFuelStopIds]],
            paint: {
              "circle-color": "#e0f2fe",
              "circle-opacity": 0.85,
              "circle-radius": 19,
              "circle-stroke-color": "#087de0",
              "circle-stroke-width": 3,
            },
          } as TrimbleMaps.CircleLayer);
        }

        map.addLayer({
          id: "poiPoints",
          type: "symbol",
          source: "poiSource",
          layout: {
            "icon-image": "poi-marker-icon",
            "icon-size": 1,
            "icon-allow-overlap": true,
            "icon-ignore-placement": true,
          } as TrimbleMaps.SymbolLayout,
        } as TrimbleMaps.SymbolLayer);

        map.on(
          "click",
          "poiPoints",
          (
            evt: TrimbleMaps.MapMouseEvent & {
              features?: TrimbleMaps.GeoJSONFeature[] | undefined;
            } & TrimbleMaps.EventData
          ) => {
            if (
              evt &&
              evt != null &&
              evt.features &&
              Array.isArray(evt.features) &&
              evt.features.length > 0 &&
              evt.features[0].properties &&
              evt.features[0].geometry.type == "Point"
            ) {
              const popupLocation = new TrimbleMaps.LngLat(
                evt.features[0].geometry.coordinates[0],
                evt.features[0].geometry.coordinates[1]
              );
              const popupContent = createFuelStopPopupContent(
                evt.features[0].properties,
                options.onSelectFuelStop
              );

              new TrimbleMaps.Popup()
                .setLngLat(popupLocation)
                .setDOMContent(popupContent)
                .addTo(map);
            }
          }
        );

        map.on("mouseenter", "poiPoints", () => {
          map.getCanvas().style.cursor = "pointer";
        });

        map.on("mouseleave", "poiPoints", () => {
          map.getCanvas().style.cursor = "";
        });
      }
    );
  }
};

export const addElementsToMap = (
  map: TrimbleMaps.Map,
  route: BasicRoute,
  preferredFuelStops: FuelLocation[],
  fuelDCLocations: FuelLocation[],
  options: FuelStopMapOptions = {}
): void => {
  addPreferredFuelStopsToMap(map, preferredFuelStops, fuelDCLocations, options);
};
